import { useEffect, useState } from 'react';
import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router';
import { sleep, setAsyncInterval, clearAsyncInterval } from '@/util/utilities';
import { removeSearchParamsFromUrl } from '@/util/http-utils';
import { restoreAppState, type SessionKeySearchParams } from '@/util/app-state';
import { getCheckoutStatus, getPlanDescription } from '@/api/service/stripe.service';
import Authorizor from '@/util/authorizor';
import jumperUrl from '@/assets/jumper.svg';
import Loading from '@/components/Loading';
import styles from './checkedout.module.css';
import { StripeCheckoutStatus, StripePaymentStatus, type StripeCheckoutSessionStatus } from '@/models/stripe';
import { SubscriptionLevel } from '@/models/profile';

const successMsg = "Thank you for subscribing to LongJump!";
const failedMessage = "Something went wrong with your checkout session.";
const failedPaymentMessage = "No payment was made or payment was unsuccessful.";
const timeoutMsg = "Payment processing timed out. Verify account status in your profile."

interface StripeSessionQueryArgs extends SessionKeySearchParams {
    session_id: string;
}

const route = '/checkedout/';

export const Route = createFileRoute(route)({
  component: CheckedOut,
})

// interface NavigateProps {
//     path: string;
// }

// const Navigate = (props: NavigateProps) => {
//     const navigate = useNavigate();

//     useEffect(() => {
//         navigate({
//             to: props.path,
//             viewTransition: true,
//         })
//     }, []);

//     return <Loading />
// }


function CheckedOut() {
    const [busy, setBusy] = useState(true);
    const [appStateRestored, setAppStateRestored] = useState(false);
    const [title, setTitle] = useState<string>(failedMessage);
    const [subscriptionMessage, setSubscriptionMessage] = useState("");
    const { key, session_id } = useSearch({ from: route }) as StripeSessionQueryArgs;
    const navigate = useNavigate();

    console.log(`Checkedout: session_id="${session_id}"`);

    useEffect(() => {
        if (!appStateRestored && key) {
            restoreAppState(key, true);
            setAppStateRestored(true);
        }
    }, []);

    useEffect(() => {
        const waitCheckoutComplete = async (): Promise<StripeCheckoutSessionStatus | undefined> => {
            const maxIteractions = 5; // 15 seconds
            let checkoutStatus: StripeCheckoutSessionStatus | undefined;
            try {
                let complete = false;
                let timedout = false;
                let iterations = 0;
                let timerId = setAsyncInterval(async () => {
                                    checkoutStatus = await getCheckoutStatus(session_id);
                                    complete = checkoutStatus?.status === StripeCheckoutStatus.COMPLETE;
                                    timedout = ++iterations > maxIteractions;
                                    if (complete || timedout) {
                                        clearAsyncInterval(timerId);
                                    }
                                }, 3000);
                while (!timedout && !complete) {
                    await sleep(500);
                }
            }
            catch(error) {;
                console.error(`Error in waitCheckoutComplete: ${JSON.stringify(error)}`)
            }
            return checkoutStatus;
        }

        const updateCachedSubscription = async () => {
            try {
                if (!session_id) {
                    // console.warn("session_id search param is undefined");
                    return;
                }
                // const sessionId = session_id;
                setBusy(true);
                removeSearchParamsFromUrl();
                // restoreAppState(true);
                const checkoutStatus = await waitCheckoutComplete();
                if (checkoutStatus?.status == StripeCheckoutStatus.OPEN) {
                    setTitle(timeoutMsg);
                }
                else if (checkoutStatus?.status == StripeCheckoutStatus.COMPLETE) {
                    if (checkoutStatus?.paymentStatus == StripePaymentStatus.PAID || checkoutStatus?.paymentStatus == StripePaymentStatus.NO_PAYMENT_REQUIRED) {
                        setTitle(successMsg);
                    } else {
                        setTitle(failedPaymentMessage)
                    }
                } else {
                    setTitle(failedMessage);
                }
                if (checkoutStatus?.paymentStatus === StripePaymentStatus.PAID || checkoutStatus?.paymentStatus === StripePaymentStatus.NO_PAYMENT_REQUIRED) {
                    await Authorizor.refreshProfile();
                    const subscription = Authorizor.profile?.subscription;
                    if (!subscription) {
                        throw new Error("Subscription not loaded!");
                    }
                    if (subscription.type === SubscriptionLevel.NONE) {
                        throw new Error("Subscription plan is NONE.")
                    }
                    setSubscriptionMessage(getPlanDescription(subscription.type))
                    setTimeout(() => {
                        navigate({ to: "/", viewTransition: true});
                    }, 5000);
                }
            } 
            catch (error) {
                console.error(`Error processing checkout: ${error}`);
            }
            finally {
                setBusy(false);
            }
        }
        if (session_id) {
            updateCachedSubscription();
        }
    }, [session_id]);

    if (busy) {
        return (
            <Loading message='Your checkout is processing' />
        )
    }

    return (
        <div className={styles.pageContainer}>
            <section className={styles.container}>
                <div className={styles.top}>
                    <img className={styles.jumper} src={jumperUrl} alt="LongJump.ai" />
                    <div className={styles.title}>
                        {title}
                    </div>
                    <div className={styles.subscriptionMsg}>
                        {subscriptionMessage}
                    </div>
                </div>
            </section>
        </div>
    );
}
